import { abortAiZipUpload, uploadZipServerSide } from "../../service/aiZipUploadService";

/**
 * Central eFolder upload queue (spec #2743 section 9).
 *
 * Shaped like `uploadQueue.js` (entries, `runZipUploads`, `tally`) but each
 * entry is a multi-GB zip sent as 100 MB multipart parts through
 * `aiZipUploadService.uploadZipServerSide`, with no matter: the backend
 * works out whose eFolder it is after the bytes arrive. Two zips at a time
 * (each already runs three part PUTs) keeps a drop of five from saturating
 * the uplink and starving every zip at once.
 */

export const DEFAULT_CONCURRENCY = 2;

export const defaultApi = {
  upload: (file, options) => uploadZipServerSide(file, options),
  abort: (jobId) => abortAiZipUpload(jobId),
};

export const isZip = (file) =>
  file.type === "application/zip" ||
  file.type === "application/x-zip-compressed" ||
  file.name.toLowerCase().endsWith(".zip");

export const fileKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

export const newBatchId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === "x" ? r : (r % 4) + 8).toString(16);
      });

// One zip the user dropped this session. `status` covers only the part the
// browser does (sending the bytes); once `job` is set the server's `stage`
// takes over. Status: queued | uploading | sent | failed | cancelled.
export const makeZipEntry = (file, batchId) => ({
  key: fileKey(file),
  file,
  name: file.name,
  size: file.size,
  batchId,
  status: "queued",
  progress: 0,
  jobId: null,
  layout: null,
  job: null,
  error: null,
  controller: null,
});

const errorText = (error) =>
  error?.response?.data?.error || error?.message || "Could not upload this zip.";

// Send one zip. The entry remembers the job id as soon as the server hands
// one out, so a cancel mid-upload can also abort the multipart upload.
export async function uploadZip(entry, { api = defaultApi, onChange = () => {} } = {}) {
  const controller = new AbortController();
  entry.controller = controller;
  entry.status = "uploading";
  entry.progress = 0;
  entry.error = null;
  onChange(entry);
  try {
    const job = await api.upload(entry.file, {
      batchId: entry.batchId,
      signal: controller.signal,
      onInitiated: (initiated) => {
        entry.jobId = initiated.job_id;
        entry.layout = initiated.layout || null;
        onChange(entry);
      },
      onProgress: (percent) => {
        if (entry.status !== "uploading") return;
        entry.progress = percent;
        onChange(entry);
      },
    });
    if (entry.status === "cancelled") return entry;
    entry.jobId = job.job_id;
    entry.job = job;
    entry.progress = 100;
    entry.status = "sent";
  } catch (error) {
    if (entry.status === "cancelled" || controller.signal.aborted) {
      entry.status = "cancelled";
    } else {
      entry.status = "failed";
      entry.error = errorText(error);
    }
  } finally {
    entry.controller = null;
    onChange(entry);
  }
  return entry;
}

// Stop a zip that is still leaving the browser. The server-side abort is
// what `uploadZipServerSide` already does on failure; a cancel that lands
// after `complete` (the job exists and is identifying) is a Skip, not this.
export async function cancelZip(entry, { api = defaultApi, onChange = () => {} } = {}) {
  if (entry.status !== "uploading" && entry.status !== "queued") return entry;
  entry.status = "cancelled";
  entry.error = null;
  if (entry.controller) entry.controller.abort();
  onChange(entry);
  return entry;
}

// Upload every queued or failed entry, two at a time. ``onChange`` fires
// after each entry changes so the page can redraw its rows.
export async function runZipUploads(
  entries,
  { concurrency = DEFAULT_CONCURRENCY, onChange = () => {}, api = defaultApi } = {}
) {
  const todo = entries.filter((e) => e.status === "queued" || e.status === "failed");
  let next = 0;
  const worker = async () => {
    while (next < todo.length) {
      const entry = todo[next];
      next += 1;
      if (entry.status === "cancelled") continue;
      await uploadZip(entry, { api, onChange });
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, todo.length) }, worker));
  return entries;
}

export const tally = (entries) => {
  const counts = { total: entries.length, queued: 0, uploading: 0, sent: 0, failed: 0, cancelled: 0 };
  entries.forEach((e) => {
    counts[e.status] += 1;
  });
  return counts;
};

// What the table shows: this session's entries (the browser knows their
// name, size and upload progress) merged with the server's job payloads,
// then every other job the list endpoint returned. One row per zip.
// (The third argument is a change counter the page passes so a memoised
// call re-runs after the queue mutates an entry in place.)
export const buildRows = (entries, jobsById, _version) => {
  const rows = [];
  const seen = new Set();
  entries.forEach((entry) => {
    const job = (entry.jobId && jobsById[entry.jobId]) || entry.job || null;
    if (job) seen.add(String(job.job_id));
    let stage;
    if (entry.status === "queued" || entry.status === "uploading") stage = "uploading";
    else if (entry.status === "failed") stage = "upload_failed";
    else if (entry.status === "cancelled") stage = job && job.stage !== "uploading" ? job.stage : "cancelled";
    else stage = job?.stage || "identifying";
    rows.push({
      key: entry.key,
      name: entry.name,
      size: entry.size,
      stage,
      progress: entry.progress,
      error: entry.error,
      job,
      entry,
      batchId: entry.batchId,
      session: true,
    });
  });
  Object.values(jobsById)
    .filter((job) => !seen.has(String(job.job_id)))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .forEach((job) => {
      rows.push({
        key: `job-${job.job_id}`,
        name: job.redact ? null : job.zip_name,
        size: null,
        stage: job.stage,
        progress: 0,
        error: null,
        job,
        entry: null,
        batchId: job.batch_id,
        session: false,
      });
    });
  return rows;
};

export const isTerminalStage = (stage) => stage === "done" || stage === "failed" || stage === "aborted";

// Batches from this session that still have a zip the server is working on
// (or one the browser has not finished sending), so the page keeps polling.
export const activeBatches = (batchIds, rows) =>
  batchIds.filter((batchId) =>
    rows.some(
      (row) =>
        row.batchId === batchId &&
        row.session &&
        (row.stage === "uploading" || (row.job && !isTerminalStage(row.stage)))
    )
  );

// Polling cadence (spec 9.2.6): every 4 s, 15 s once a batch has been
// running ten minutes. The backend caches `counts` for 5 s per job.
export const POLL_FAST_MS = 4000;
export const POLL_SLOW_MS = 15000;
export const POLL_BACKOFF_AFTER_MS = 10 * 60 * 1000;

export const pollIntervalFor = (startedAt, now = Date.now()) =>
  now - startedAt >= POLL_BACKOFF_AFTER_MS ? POLL_SLOW_MS : POLL_FAST_MS;
